import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { getInlineCitationLinkClassName, linkCitationChunk } from '../../utils/citationDisplay';
import styles from './css/index.module.css';

function normalizeCitationRef(value) {
    const raw = String(value || '').trim();
    const bracketed = raw.match(/^\[(\d+)\]$/);
    if (bracketed) return bracketed[1];
    const numeric = raw.match(/^(\d+)$/);
    return numeric ? numeric[1] : null;
}

function linkifyCitationRefs(text, citations, citationScope) {
    if (!citationScope || !Array.isArray(citations) || citations.length === 0) return text;
    const availableRefs = new Set(citations.map(c => normalizeCitationRef(c?.ref ?? c)).filter(Boolean));
    if (availableRefs.size === 0) return text;

    const normalizedText = text.replace(/\[(\d+)\]\s*-\s*\[(\d+)\]/g, (match, startRef, endRef) => {
        if (!availableRefs.has(startRef) || !availableRefs.has(endRef)) return match;
        return `[${startRef}], [${endRef}]`;
    });

    return normalizedText.replace(/\[(\d+)\]/g, (match, ref) => {
        if (!availableRefs.has(ref)) return match;
        return `[${match}](#ai-warden-${citationScope}-citation-${ref})`;
    });
}

function findCitationByRef(citations, ref) {
    const normalized = normalizeCitationRef(ref);
    if (!normalized) return null;
    return (Array.isArray(citations) ? citations : []).find(c => normalizeCitationRef(c?.ref ?? c) === normalized) || null;
}

/** Renders RAG final_answer as Markdown (lists, headings, emphasis, links). */
export default function AiWardenMarkdownReport({ content, citations = [], chunks = [], citationScope = '', onCitationClick }) {
    const text = typeof content === 'string' ? content : '';
    const renderedText = useMemo(() => linkifyCitationRefs(text, citations, citationScope), [text, citations, citationScope]);
    if (!text.trim()) return null;
    return (
        <div className={styles.aiWardenMarkdown}>
            <ReactMarkdown
                components={{
                    a: ({ href, children, ...props }) => {
                        const anchorProps = { ...props };
                        delete anchorProps.node;
                        const match = typeof href === 'string' ? href.match(/^#ai-warden-[a-z0-9-]+-citation-(\d+)$/i) : null;
                        if (match) {
                            const cite = findCitationByRef(citations, match[1]);
                            const linkedChunk = cite ? linkCitationChunk(cite, chunks) : null;
                            const linkClass = getInlineCitationLinkClassName(styles, cite, linkedChunk);
                            return (
                                <a
                                    {...anchorProps}
                                    href={href}
                                    className={linkClass || styles.aiWardenInlineCitationLink}
                                    onClick={e => {
                                        if (!onCitationClick) return;
                                        e.preventDefault();
                                        onCitationClick?.(match[1], cite, linkedChunk);
                                    }}>
                                    {children}
                                </a>
                            );
                        }
                        return (
                            <a {...anchorProps} href={href}>
                                {children}
                            </a>
                        );
                    },
                }}>
                {renderedText}
            </ReactMarkdown>
        </div>
    );
}
